import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLeague } from "../../hooks/useLeague";
import { useToast } from "../../context/ToastContext";
import { apiPatch, apiPost } from "../../utils/apiClient";
import { getPenaltiesUrl, getPenaltyUrl } from "../../config/api";
import { Game, Penalty, PenaltyType } from "../../types/models";
import EntryPanel, { EntryStep } from "./EntryPanel";
import PlayerGrid from "./PlayerGrid";
import OptionGrid from "./OptionGrid";
import ClockEntry from "./ClockEntry";
import { TeamRoster } from "./players";
import { splitPenaltyTypes } from "./penaltyTypes";
import { Period, PERIOD_OPTIONS, formatPeriod } from "./periods";
import { ClockValue, clockError, formatClock, splitClock } from "./clock";

/**
 * The order the steps are asked in. Length is not in the flow: it follows
 * from the penalty type, and is reached by its chip when a call runs long.
 */
const FLOW = ["player", "type", "period", "time"];

/** Used when a penalty type carries no standard length of its own. */
const DEFAULT_PENALTY_LENGTH = 2;

const PENALTY_LENGTH_CHOICES = [2, 4, 5, 10, 20];

interface PenaltyEntryProps {
  game: Game;
  subtitle: string;
  rosters: TeamRoster[];
  penaltyTypes: PenaltyType[];
  editingPenalty: Penalty | null;
  onSaved: () => void;
  onCancel: () => void;
}

function PenaltyEntry({
  game,
  subtitle,
  rosters,
  penaltyTypes,
  editingPenalty,
  onSaved,
  onCancel,
}: PenaltyEntryProps) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { showToast } = useToast();

  const [teamId, setTeamId] = useState<number | null>(
    editingPenalty?.team_id ?? null,
  );
  const [playerId, setPlayerId] = useState<number | null>(
    editingPenalty?.penalty_player_id ?? null,
  );
  const [penaltyTypeId, setPenaltyTypeId] = useState<number | null>(
    editingPenalty?.penalty_type_id ?? null,
  );
  const [penaltyLength, setPenaltyLength] = useState<number>(
    editingPenalty?.penalty_length ?? DEFAULT_PENALTY_LENGTH,
  );
  const [period, setPeriod] = useState<Period>(
    editingPenalty?.period ?? "period1",
  );
  const [clock, setClock] = useState<ClockValue>(
    splitClock(editingPenalty?.off_time_formatted),
  );
  const [activeStepKey, setActiveStepKey] = useState(FLOW[0]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const playersById = useMemo(
    () =>
      new Map(
        rosters.flatMap((roster) =>
          roster.players.map((player) => [player.id, player] as const),
        ),
      ),
    [rosters],
  );

  // The common calls become buttons; the rest go into a list below them, so the
  // dozen used every game are one tap away without losing any of the others.
  const { common: commonTypes, rest: otherTypes } = useMemo(
    () => splitPenaltyTypes(penaltyTypes),
    [penaltyTypes],
  );

  const commonTypeOptions = useMemo(
    () =>
      commonTypes.map((type) => ({
        value: type.id,
        label: type.name,
        sublabel: type.abbreviation,
      })),
    [commonTypes],
  );

  const selectedIsCommon = commonTypes.some(
    (type) => type.id === penaltyTypeId,
  );

  // Always offer the common lengths, plus whatever this penalty already has.
  const lengthOptions = useMemo(() => {
    const values = new Set([...PENALTY_LENGTH_CHOICES, penaltyLength]);
    return Array.from(values)
      .sort((a, b) => a - b)
      .map((minutes) => ({ value: minutes, label: `${minutes} min` }));
  }, [penaltyLength]);

  const goToNextStep = (key: string) => {
    const index = FLOW.indexOf(key);
    if (index >= 0 && index < FLOW.length - 1) {
      setActiveStepKey(FLOW[index + 1]);
    }
  };

  const handlePlayerSelect = (selectedId: number, selectedTeamId: number) => {
    setPlayerId(selectedId);
    setTeamId(selectedTeamId);
    goToNextStep("player");
  };

  const handleTypeSelect = (typeId: number) => {
    setPenaltyTypeId(typeId);
    // Each penalty type carries its standard length, so choosing the call
    // fills the length in; the Length chip overrides it when needed.
    const selectedType = penaltyTypes.find((type) => type.id === typeId);
    setPenaltyLength(selectedType?.length ?? DEFAULT_PENALTY_LENGTH);
    goToNextStep("type");
  };

  const handleSave = () => {
    setErrorMessage(null);

    if (playerId === null || teamId === null) {
      setErrorMessage("Choose who took the penalty.");
      setActiveStepKey("player");
      return;
    }

    if (penaltyTypeId === null) {
      setErrorMessage("Choose the call.");
      setActiveStepKey("type");
      return;
    }

    const invalidClock = clockError(clock);
    if (invalidClock) {
      setErrorMessage(invalidClock);
      setActiveStepKey("time");
      return;
    }

    setIsSubmitting(true);

    const requestBody = {
      penalty: {
        team_id: teamId,
        penalty_player_id: playerId,
        penalty_type_id: penaltyTypeId,
        period,
        off_time: formatClock(clock),
        penalty_length: penaltyLength,
      },
    };

    const url = editingPenalty
      ? getPenaltyUrl(editingPenalty.id)
      : getPenaltiesUrl(game.id);
    const apiFn = editingPenalty ? apiPatch : apiPost;

    apiFn(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        showToast(
          editingPenalty ? "Penalty updated" : "Penalty added",
          "success",
        );
        onSaved();
      })
      .catch((error) => {
        setErrorMessage(error.message || "An error occurred");
        setIsSubmitting(false);
      });
  };

  const selectedPlayer = playerId === null ? null : playersById.get(playerId);
  const selectedType = penaltyTypes.find((type) => type.id === penaltyTypeId);

  const steps: EntryStep[] = [
    {
      key: "player",
      chipLabel: "Penalty",
      chipValue:
        playerId === null
          ? null
          : selectedPlayer
            ? `#${selectedPlayer.jerseyLabel} ${selectedPlayer.name}`
            : `Player ${playerId}`,
      prompt: "Who took the penalty?",
      render: () => (
        <PlayerGrid
          rosters={rosters}
          selectedPlayerId={playerId}
          onSelect={handlePlayerSelect}
        />
      ),
    },
    {
      key: "type",
      chipLabel: "Call",
      chipValue: selectedType ? selectedType.name : null,
      prompt: "What was the call?",
      render: () => (
        <>
          <OptionGrid
            options={commonTypeOptions}
            selected={selectedIsCommon ? penaltyTypeId : null}
            onSelect={handleTypeSelect}
          />
          <label className="ee-more-calls">
            <span className="ee-clock-caption">More calls</span>
            <select
              className="ee-search"
              value={
                selectedIsCommon || penaltyTypeId === null ? "" : penaltyTypeId
              }
              onChange={(event) => {
                if (event.target.value === "") return;
                handleTypeSelect(Number(event.target.value));
              }}
            >
              <option value="">Majors, misconducts and the rest...</option>
              {otherTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </label>
        </>
      ),
    },
    {
      key: "length",
      chipLabel: "Length",
      chipValue: `${penaltyLength} min`,
      prompt: "How long is the penalty?",
      render: () => (
        <OptionGrid
          options={lengthOptions}
          selected={penaltyLength}
          onSelect={setPenaltyLength}
          narrow
        />
      ),
    },
    {
      key: "period",
      chipLabel: "Period",
      chipValue: formatPeriod(period),
      prompt: "Which period?",
      render: () => (
        <OptionGrid
          options={PERIOD_OPTIONS}
          selected={period}
          onSelect={(value) => {
            setPeriod(value);
            goToNextStep("period");
          }}
        />
      ),
    },
    {
      key: "time",
      chipLabel: "Off time",
      chipValue:
        clock.minutes === "" && clock.seconds === ""
          ? null
          : formatClock(clock),
      prompt: "What did the clock say?",
      render: () => <ClockEntry value={clock} onChange={setClock} />,
    },
  ];

  return (
    <EntryPanel
      title={editingPenalty ? "Edit penalty" : "Add penalty"}
      subtitle={subtitle}
      steps={steps}
      activeStepKey={activeStepKey}
      onStepSelect={setActiveStepKey}
      errorMessage={errorMessage}
      isSubmitting={isSubmitting}
      canSave={playerId !== null && penaltyTypeId !== null}
      saveLabel="Save penalty"
      onSave={handleSave}
      onCancel={onCancel}
    />
  );
}

export default PenaltyEntry;
